import { getMessaging, getToken, onMessage, deleteToken } from 'firebase/messaging';
import { httpsCallable } from 'firebase/functions';
import { getClientFunctions } from './firebase-client';
import { app } from './firebase-client';

const VAPID_KEY = 'BLYrCvZ_a3H7R8-qZoYs2MxQ7QVxL6NWb3T8VRuLZjD5HXqKc7JZQ8gPV0RxN4WmJ6Y9T3ZvLxR5'; // You'll need to generate this

let messaging: ReturnType<typeof getMessaging> | null = null;

/**
 * Initialize Firebase Cloud Messaging
 */
export function initializeMessaging() {
  try {
    messaging = getMessaging(app);
    return messaging;
  } catch (error) {
    console.error('Error initializing Firebase Messaging:', error);
    return null;
  }
}

/**
 * Check if push notifications are supported
 */
export function isPushNotificationSupported(): boolean {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission(): NotificationPermission {
  if (!isPushNotificationSupported()) {
    return 'denied';
  }
  return Notification.permission;
}

/**
 * Request notification permission from user
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!isPushNotificationSupported()) {
    throw new Error('Push notifications are not supported in this browser');
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    throw new Error('Notification permission has been denied. Please enable it in your browser settings.');
  }

  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Register service worker for FCM
 */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers are not supported');
  }

  try {
    // Register the service worker
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/',
    });

    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;

    console.log('Service Worker registered successfully:', registration);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    throw error;
  }
}

/**
 * Get FCM token for this device
 */
export async function getFCMToken(): Promise<string | null> {
  try {
    // Ensure messaging is initialized
    if (!messaging) {
      messaging = initializeMessaging();
    }

    if (!messaging) {
      throw new Error('Firebase Messaging not initialized');
    }

    // Request permission if not granted
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.log('Notification permission not granted');
      return null;
    }

    // Register service worker
    const registration = await registerServiceWorker();

    // Get FCM token
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      console.log('FCM token retrieved:', token.substring(0, 20) + '...');
      return token;
    } else {
      console.log('No FCM token available');
      return null;
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    throw error;
  }
}

/**
 * Save FCM token to user's Firestore document
 */
export async function saveFCMToken(token: string): Promise<void> {
  try {
    const functions = getClientFunctions();
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    const saveFCMTokenCallable = httpsCallable<{ token: string }, { success: boolean }>(
      functions,
      'saveFCMToken'
    );

    const result = await saveFCMTokenCallable({ token });
    console.log('FCM token saved to Firestore:', result.data);
  } catch (error) {
    console.error('Error saving FCM token:', error);
    throw error;
  }
}

/**
 * Remove FCM token from user's Firestore document
 */
export async function removeFCMToken(token: string): Promise<void> {
  try {
    const functions = getClientFunctions();
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    const removeFCMTokenCallable = httpsCallable<{ token: string }, { success: boolean }>(
      functions,
      'removeFCMToken'
    );

    await removeFCMTokenCallable({ token });
    console.log('FCM token removed from Firestore');
  } catch (error) {
    console.error('Error removing FCM token:', error);
    throw error;
  }
}

/**
 * Delete FCM token from FCM service
 */
export async function deleteFCMToken(): Promise<void> {
  try {
    if (!messaging) {
      messaging = initializeMessaging();
    }

    if (!messaging) {
      throw new Error('Firebase Messaging not initialized');
    }

    await deleteToken(messaging);
    console.log('FCM token deleted');
  } catch (error) {
    console.error('Error deleting FCM token:', error);
    throw error;
  }
}

/**
 * Setup FCM for the current user
 * Requests permission, gets token, and saves to Firestore
 */
export async function setupFCM(): Promise<string | null> {
  try {
    const token = await getFCMToken();
    if (token) {
      await saveFCMToken(token);
      return token;
    }
    return null;
  } catch (error) {
    console.error('Error setting up FCM:', error);
    throw error;
  }
}

/**
 * Setup foreground message handler
 * Returns unsubscribe function
 */
export function onForegroundMessage(
  handler: (payload: unknown) => void
): (() => void) | null {
  try {
    if (!messaging) {
      messaging = initializeMessaging();
    }

    if (!messaging) {
      console.error('Firebase Messaging not initialized');
      return null;
    }

    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload);
      handler(payload);
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up foreground message handler:', error);
    return null;
  }
}

/**
 * Show browser notification (for foreground messages)
 */
export function showBrowserNotification(
  title: string,
  options: NotificationOptions = {}
): void {
  if (!isPushNotificationSupported()) {
    console.warn('Browser notifications not supported');
    return;
  }

  if (Notification.permission === 'granted') {
    new Notification(title, {
      icon: '/vite.svg',
      badge: '/vite.svg',
      ...options,
    });
  }
}
